import type { CSSProperties, ReactNode } from 'react';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { ArcToolbar } from '../components/arc/primitives/arc-primitives';

export interface ServiceBarOption {
  value: string;
  label: string;
}

export interface ServiceBarSelect {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ServiceBarOption[];
  minWidth?: number;
}

export interface ServiceBarProps {
  className?: string;
  selects: ServiceBarSelect[];
  onReset?: () => void;
  search?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  extra?: ReactNode;
  right?: ReactNode;
  variant?: 'compact' | 'seg';
}

interface VariantStyle {
  barFont: string;
  gap: number;
  barClass: string;
  labelUpper: boolean;
  label: CSSProperties;
  select: CSSProperties;
  reset: CSSProperties;
  search: CSSProperties;
}

const VARIANTS: Record<'compact' | 'seg', VariantStyle> = {
  compact: {
    barFont: '0.74rem',
    gap: 8,
    barClass: 'ru-toolbar-compact',
    labelUpper: true,
    label: { fontSize: '0.62rem', fontWeight: 600 },
    select: { fontSize: '0.74rem', width: 'auto', padding: '0.15rem 1.4rem 0.15rem 0.4rem', lineHeight: 1.25 },
    reset: { fontSize: '0.7rem', padding: '0.1rem 0.3rem' },
    search: { fontSize: '0.74rem', flex: 1, minWidth: 0, padding: '0.15rem 0.5rem', lineHeight: 1.25 },
  },
  seg: {
    barFont: '0.85rem',
    gap: 10,
    barClass: 'seg-toolbar',
    labelUpper: false,
    label: { fontSize: '0.78rem', fontWeight: 500 },
    select: { fontSize: '0.85rem', width: 'auto', height: 32, padding: '4px 28px 4px 10px', lineHeight: '20px' },
    reset: { fontSize: '0.78rem', padding: '4px 8px' },
    search: { fontSize: '0.85rem', flex: 1, minWidth: 0, height: 32, padding: '4px 10px', lineHeight: '20px' },
  },
};

export function ServiceBar({ className, selects, onReset, search, onSearch, searchPlaceholder, extra, right, variant }: ServiceBarProps) {
  const v = VARIANTS[variant ?? 'compact'];
  const mode = variant ?? 'compact';
  return (
    <ArcToolbar
      className={`service-bar service-bar-${mode} ${v.barClass}${className ? ` ${className}` : ''}`}
      label="Filtry"
      end={(
        <div className="service-bar-end">
          {onSearch && (
            <label className="service-search">
              <i data-feather="search" aria-hidden="true" />
              <input
                type="search"
                placeholder={searchPlaceholder ?? 'jméno / email / telefon…'}
                aria-label="Hledat"
                value={search ?? ''}
                onChange={(e) => onSearch(e.target.value)}
              />
            </label>
          )}
          {right && <div className="service-actions">{right}</div>}
        </div>
      )}
    >
      <div className="service-bar-filters">
          {selects.map((s) => (
            <div className="service-filter" key={s.label} style={s.minWidth != null ? { minWidth: s.minWidth } : undefined}>
              <span>{s.label}</span>
              <CompactSelect
                className="service-filter-select"
                value={s.value}
                onChange={s.onChange}
                options={s.options}
                ariaLabel={s.label}
              />
            </div>
          ))}

          {extra}

          {onReset && (
            <button type="button" className="service-reset" onClick={onReset}>
              Vyčistit
            </button>
          )}
      </div>
    </ArcToolbar>
  );
}
